import dagre from '@dagrejs/dagre'
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
} from 'd3-force'
import type { Edge } from '@xyflow/react'
import {
  getNodeDimensions,
  resolveCollisions,
  withNodeDimensions,
  type GraphNode,
} from './memory-graph-collisions'

function getCollisionRadius(node: GraphNode): number {
  const { width, height } = getNodeDimensions(node)
  return Math.max(width, height) / 2 + 16
}

function findConnectedComponents(nodeIds: string[], edges: Edge[]): string[][] {
  const adj = new Map<string, Set<string>>()
  nodeIds.forEach((id) => adj.set(id, new Set()))
  edges.forEach((edge) => {
    adj.get(edge.source)?.add(edge.target)
    adj.get(edge.target)?.add(edge.source)
  })

  const visited = new Set<string>()
  const components: string[][] = []

  nodeIds.forEach((id) => {
    if (visited.has(id)) return
    const stack = [id]
    const component: string[] = []
    visited.add(id)

    while (stack.length > 0) {
      const current = stack.pop()!
      component.push(current)
      adj.get(current)?.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          stack.push(neighbor)
        }
      })
    }

    components.push(component)
  })

  return components.sort((a, b) => b.length - a.length)
}

function getBoundingBoxForNodes(nodes: GraphNode[]) {
  if (nodes.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  nodes.forEach((node) => {
    const { width, height } = getNodeDimensions(node)
    minX = Math.min(minX, node.position.x)
    minY = Math.min(minY, node.position.y)
    maxX = Math.max(maxX, node.position.x + width)
    maxY = Math.max(maxY, node.position.y + height)
  })

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

const COLUMN_GAP = 36
const MEMORY_ROW_GAP = 18
const ENTITY_MEMORY_GAP = 24
const COMPONENT_GAP = 44

type PackedComponent = {
  nodes: GraphNode[]
  box: ReturnType<typeof getBoundingBoxForNodes>
}

function normalizeComponent(nodes: GraphNode[]): PackedComponent {
  const bbox = getBoundingBoxForNodes(nodes)
  const normalized = nodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x - bbox.minX,
      y: node.position.y - bbox.minY,
    },
  }))
  return { nodes: normalized, box: getBoundingBoxForNodes(normalized) }
}

function placeComponent(
  component: PackedComponent,
  offsetX: number,
  offsetY: number,
): GraphNode[] {
  return component.nodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x + offsetX,
      y: node.position.y + offsetY,
    },
  }))
}

/**
 * Shelf-pack disconnected clusters: each row uses actual component widths,
 * not the global max width (which caused huge horizontal gaps).
 */
function tileComponents(componentNodes: GraphNode[][], gap = COMPONENT_GAP): GraphNode[] {
  if (componentNodes.length === 0) return []
  if (componentNodes.length === 1) return componentNodes[0]

  const components = componentNodes
    .map(normalizeComponent)
    .sort((a, b) => b.box.width * b.box.height - a.box.width * a.box.height)

  const cols = Math.ceil(Math.sqrt(components.length))
  const rowWidths: number[] = []

  components.forEach((component, index) => {
    const row = Math.floor(index / cols)
    rowWidths[row] = (rowWidths[row] ?? 0) + component.box.width
  })

  const targetRowWidth =
    Math.max(...rowWidths, 0) / Math.max(1, Math.min(cols, components.length))

  const placed: GraphNode[] = []
  let rowX = 0
  let rowY = 0
  let rowMaxHeight = 0

  components.forEach((component) => {
    if (rowX > 0 && rowX + component.box.width > targetRowWidth) {
      rowY += rowMaxHeight + gap
      rowX = 0
      rowMaxHeight = 0
    }

    placed.push(...placeComponent(component, rowX, rowY))
    rowX += component.box.width + gap
    rowMaxHeight = Math.max(rowMaxHeight, component.box.height)
  })

  return placed
}

/** Nudge disconnected cluster islands closer with a short meta-node force pass. */
function relaxComponentSpacing(nodes: GraphNode[], edges: Edge[], gap = COMPONENT_GAP): GraphNode[] {
  const components = findConnectedComponents(
    nodes.map((n) => n.id),
    edges,
  )
  if (components.length <= 1) return nodes

  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const packed = components.map((ids) => {
    const componentNodes = ids.map((id) => nodeById.get(id)!)
    const normalized = normalizeComponent(componentNodes)
    return { ids, ...normalized }
  })

  type MetaNode = { id: number; x: number; y: number; r: number }
  const meta: MetaNode[] = packed.map((component, index) => ({
    id: index,
    x: component.box.minX + component.box.width / 2,
    y: component.box.minY + component.box.height / 2,
    r: Math.max(component.box.width, component.box.height) / 2 + gap / 2,
  }))

  const centerX = meta.reduce((sum, node) => sum + node.x, 0) / meta.length
  const centerY = meta.reduce((sum, node) => sum + node.y, 0) / meta.length

  const simulation = forceSimulation(meta)
    .force('charge', forceManyBody<MetaNode>().strength(-90))
    .force('center', forceCenter(centerX, centerY).strength(0.06))
    .force(
      'collision',
      forceCollide<MetaNode>()
        .radius((d) => d.r)
        .strength(0.95)
        .iterations(3),
    )

  simulation.stop()
  for (let i = 0; i < 140; i += 1) {
    simulation.tick()
  }

  const offsets = new Map<number, { dx: number; dy: number }>()
  packed.forEach((_, index) => {
    const before = meta[index]
    const after = simulation.nodes()[index]
    offsets.set(index, {
      dx: (after.x ?? before.x) - before.x,
      dy: (after.y ?? before.y) - before.y,
    })
  })

  const componentIndexByNodeId = new Map<string, number>()
  packed.forEach((component, index) => {
    component.ids.forEach((id) => componentIndexByNodeId.set(id, index))
  })

  return nodes.map((node) => {
    const componentIndex = componentIndexByNodeId.get(node.id)
    if (componentIndex === undefined) return node
    const offset = offsets.get(componentIndex) ?? { dx: 0, dy: 0 }
    return {
      ...node,
      position: {
        x: node.position.x + offset.dx,
        y: node.position.y + offset.dy,
      },
    }
  })
}

/** Center graph in viewport without scaling (scaling caused cluster squeeze). */
function centerInViewport(nodes: GraphNode[], width: number, height: number) {
  const bbox = getBoundingBoxForNodes(nodes)
  if (bbox.width === 0 && bbox.height === 0) return nodes

  const centerX = bbox.minX + bbox.width / 2
  const centerY = bbox.minY + bbox.height / 2

  return nodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x - centerX + width / 2,
      y: node.position.y - centerY + height / 2,
    },
  }))
}

/**
 * Column layout for memory–entity stars: entity on top, linked memories stacked below.
 * Matches how this graph is structured better than generic force/dagre.
 */
function applyHubColumnLayout(nodes: GraphNode[], edges: Edge[]): GraphNode[] {
  const entityNodes = nodes.filter((n) => n.type === 'entity')
  const memoryNodes = nodes.filter((n) => n.type === 'memory')

  if (entityNodes.length === 0) {
    return applyForceComponent(nodes, edges)
  }

  const memoriesByEntity = new Map<string, GraphNode[]>()
  const orphanMemories: GraphNode[] = []

  memoryNodes.forEach((memory) => {
    const entityTargets = edges
      .filter((e) => e.source === memory.id && e.target.startsWith('entity-'))
      .map((e) => e.target)

    if (entityTargets.length > 0) {
      const primaryEntity = entityTargets[0]
      const list = memoriesByEntity.get(primaryEntity) ?? []
      list.push(memory)
      memoriesByEntity.set(primaryEntity, list)
    } else {
      orphanMemories.push(memory)
    }
  })

  const sortedEntities = [...entityNodes].sort(
    (a, b) => (memoriesByEntity.get(b.id)?.length ?? 0) - (memoriesByEntity.get(a.id)?.length ?? 0),
  )

  const positioned = new Map<string, { x: number; y: number }>()
  let currentX = 0

  sortedEntities.forEach((entity) => {
    const memories = memoriesByEntity.get(entity.id) ?? []
    const entityDim = getNodeDimensions(entity)
    const memoryWidths = memories.map((m) => getNodeDimensions(m).width)
    const colWidth = Math.max(entityDim.width, ...memoryWidths, 0)

    positioned.set(entity.id, {
      x: currentX + (colWidth - entityDim.width) / 2,
      y: 0,
    })

    let y = entityDim.height + ENTITY_MEMORY_GAP
    memories.forEach((memory) => {
      const memDim = getNodeDimensions(memory)
      positioned.set(memory.id, {
        x: currentX + (colWidth - memDim.width) / 2,
        y,
      })
      y += memDim.height + MEMORY_ROW_GAP
    })

    currentX += colWidth + COLUMN_GAP
  })

  orphanMemories.forEach((memory) => {
    const memDim = getNodeDimensions(memory)
    positioned.set(memory.id, { x: currentX, y: 0 })
    currentX += memDim.width + COLUMN_GAP
  })

  return nodes.map((node) => ({
    ...node,
    position: positioned.get(node.id) ?? node.position,
  }))
}

function layoutByComponents(
  nodes: GraphNode[],
  edges: Edge[],
  mode: 'force' | 'hierarchical',
  direction: 'LR' | 'TB' = 'TB',
): GraphNode[] {
  const components = findConnectedComponents(
    nodes.map((n) => n.id),
    edges,
  )

  if (components.length <= 1) {
    return layoutComponent(nodes, edges, mode, direction)
  }

  const laidOutComponents = components.map((ids) => {
    const idSet = new Set(ids)
    const subNodes = withNodeDimensions(nodes.filter((n) => idSet.has(n.id)))
    const subEdges = edges.filter((e) => idSet.has(e.source) && idSet.has(e.target))
    return layoutComponent(subNodes, subEdges, mode, direction)
  })

  const tiled = tileComponents(laidOutComponents)
  return relaxComponentSpacing(tiled, edges)
}

function applyDagreComponent(
  nodes: GraphNode[],
  edges: Edge[],
  direction: 'LR' | 'TB' = 'LR',
): GraphNode[] {
  const graph = new dagre.graphlib.Graph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({ rankdir: direction, nodesep: 64, ranksep: 96 })

  nodes.forEach((node) => {
    const { width, height } = getNodeDimensions(node)
    graph.setNode(node.id, { width, height })
  })

  edges.forEach((edge) => {
    graph.setEdge(edge.source, edge.target)
  })

  dagre.layout(graph)

  return nodes.map((node) => {
    const pos = graph.node(node.id)
    const { width, height } = getNodeDimensions(node)
    return {
      ...node,
      position: {
        x: pos.x - width / 2,
        y: pos.y - height / 2,
      },
    }
  })
}

type SimNode = {
  id: string
  x?: number
  y?: number
  fx?: number | null
  fy?: number | null
}

function isEntityEdge(edge: Edge): boolean {
  return edge.data?.isEntityEdge === true || edge.target.startsWith('entity-')
}

function runForceSimulation(
  nodes: GraphNode[],
  edges: Edge[],
  {
    pinEntityIds,
    ticks = 500,
    chargeStrength = -220,
    centerStrength = 0.08,
  }: {
    pinEntityIds?: Set<string>
    ticks?: number
    chargeStrength?: number
    centerStrength?: number
  } = {},
): GraphNode[] {
  if (nodes.length === 0) return nodes

  const radiusById = new Map(nodes.map((node) => [node.id, getCollisionRadius(node)]))

  const simNodes: SimNode[] = nodes.map((node, index) => {
    const { width, height } = getNodeDimensions(node)
    const x = (node.position?.x ?? 0) + width / 2
    const y = (node.position?.y ?? 0) + height / 2
    const center = {
      id: node.id,
      x: x || (index % 6) * 90 + 100,
      y: y || Math.floor(index / 6) * 72 + 100,
    }
    if (pinEntityIds?.has(node.id)) {
      return { ...center, fx: center.x, fy: center.y }
    }
    return center
  })

  const simLinks = edges.map((edge) => ({
    source: edge.source,
    target: edge.target,
    isEntity: isEntityEdge(edge),
  }))

  const centerX =
    simNodes.reduce((sum, node) => sum + (node.x ?? 0), 0) / Math.max(simNodes.length, 1)
  const centerY =
    simNodes.reduce((sum, node) => sum + (node.y ?? 0), 0) / Math.max(simNodes.length, 1)

  const simulation = forceSimulation(simNodes)
    .force(
      'link',
      forceLink(simLinks)
        .id((d) => (d as SimNode).id)
        .distance((link) => ((link as { isEntity?: boolean }).isEntity ? 72 : 96))
        .strength((link) => ((link as { isEntity?: boolean }).isEntity ? 0.55 : 0.35)),
    )
    .force('charge', forceManyBody().strength(chargeStrength))
    .force('center', forceCenter(centerX, centerY).strength(centerStrength))
    .force(
      'collision',
      forceCollide<SimNode>()
        .radius((d) => radiusById.get(d.id) ?? 64)
        .strength(0.85)
        .iterations(2),
    )

  simulation.stop()
  for (let i = 0; i < ticks; i += 1) {
    simulation.tick()
  }

  const positionById = new Map(
    simNodes.map((n) => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }]),
  )

  return nodes.map((node) => {
    const center = positionById.get(node.id) ?? { x: 0, y: 0 }
    const { width, height } = getNodeDimensions(node)
    return {
      ...node,
      position: {
        x: center.x - width / 2,
        y: center.y - height / 2,
      },
    }
  })
}

function applyForceComponent(nodes: GraphNode[], edges: Edge[]): GraphNode[] {
  return runForceSimulation(nodes, edges, { ticks: 500, chargeStrength: -220 })
}

/** Hub columns as seeds, then gentle force so memory–memory links feel organic. */
function applyNaturalEntityLayout(nodes: GraphNode[], edges: Edge[]): GraphNode[] {
  const seeded = applyHubColumnLayout(nodes, edges)
  const entityIds = new Set(nodes.filter((n) => n.type === 'entity').map((n) => n.id))
  return runForceSimulation(seeded, edges, {
    pinEntityIds: entityIds,
    ticks: 320,
    chargeStrength: -180,
    centerStrength: 0.05,
  })
}

function finalizeLayout(nodes: GraphNode[], width: number, height: number): GraphNode[] {
  const centered = centerInViewport(withNodeDimensions(nodes), width, height)
  return resolveCollisions(centered, {
    iterations: 160,
    margin: 16,
    overlapThreshold: 0.5,
  })
}

function layoutComponent(
  nodes: GraphNode[],
  edges: Edge[],
  mode: 'force' | 'hierarchical',
  direction: 'LR' | 'TB' = 'TB',
): GraphNode[] {
  const hasEntity = nodes.some((n) => n.type === 'entity')
  if (hasEntity) {
    return mode === 'hierarchical'
      ? applyHubColumnLayout(nodes, edges)
      : applyNaturalEntityLayout(nodes, edges)
  }
  return mode === 'hierarchical'
    ? applyDagreComponent(nodes, edges, direction)
    : applyForceComponent(nodes, edges)
}

export function applyDagreLayout(
  nodes: GraphNode[],
  edges: Edge[],
  width: number,
  height: number,
  direction: 'LR' | 'TB' = 'LR',
): GraphNode[] {
  const laidOut = layoutByComponents(withNodeDimensions(nodes), edges, 'hierarchical', direction)
  return finalizeLayout(laidOut, width, height)
}

export function applyForceLayout(
  nodes: GraphNode[],
  edges: Edge[],
  width: number,
  height: number,
): GraphNode[] {
  const laidOut = layoutByComponents(withNodeDimensions(nodes), edges, 'force')
  return finalizeLayout(laidOut, width, height)
}
